import UploadIcon from 'assets/icons/controls/UploadIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import Button from 'components/buttons/Button';
import { FlexCol, Flex, BodyRegular, FlexColMdFitContent } from 'components/Elements';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import styled from 'styled-components';
import { sizes, breakpoints, theme } from 'theme';
import { LogoImage } from '../shared/styles';
import strings from '../../../strings/strings.json';
import { useNavigate } from 'react-router-dom';
import { OrganisationOverviewContext } from 'contextProviders/OrganisationOverviewContext';
import useDeleteRequest from 'hooks/useDeleteRequest';
import React, { useContext } from 'react';
import { logError } from 'lib/debug-helpers';
import Alert from 'components/alert/Alert';

export default function UpdateLogo() {
    const navigate = useNavigate();
    const { organisation, refetchOrganisation } = useContext(OrganisationOverviewContext);
    const { deleteData: deleteLogo } = useDeleteRequest(`organisations/${organisation?.id}/logo`);

    const [removeLogoWarning, setRemoveLogoWarning] = React.useState<boolean>(false);
    const [removeLogoError, setRemoveLogoError] = React.useState<boolean>(false);

    if (!organisation) return <></>;

    const logoS3Filepath = organisation.logoS3Filepath;

    async function RemoveLogo() {
        const response = await deleteLogo();

        if (response.errors) {
            logError(response.errors);
            setRemoveLogoError(true);
            return;
        }

        await refetchOrganisation();
        setRemoveLogoWarning(false);
    }

    return (
        <FlexColMdFitContent>
            <BodyRegular $bold>{strings.organisationOverviewPage.organisationLogo}</BodyRegular>
            <HeaderLogoAndButtonsContainer>
                <HeaderButtonContainer>
                    <LogoAndButtonsRow>
                        {logoS3Filepath && (
                            <LogoContainer>
                                <LogoImage
                                    src={`${DOCUMENTS_URL}/${logoS3Filepath}`}
                                    alt={strings.organisationOverviewPage.logoAltText}
                                />
                            </LogoContainer>
                        )}
                        <ButtonColumn>
                            <Button
                                title={
                                    logoS3Filepath
                                        ? strings.organisationOverviewPage.updateButton.title
                                        : strings.organisationOverviewPage.uploadButton.title
                                }
                                aria={
                                    logoS3Filepath
                                        ? strings.organisationOverviewPage.updateButton.aria
                                        : strings.organisationOverviewPage.uploadButton.aria
                                }
                                icon={<UploadIcon size={IconSize.SMALL} />}
                                onClick={() => {
                                    navigate(`/organisation/logo`);
                                }}
                            />
                            {logoS3Filepath && (
                                <Button
                                    title={strings.organisationOverviewPage.removeLogoButton.title}
                                    aria={strings.organisationOverviewPage.removeLogoButton.aria}
                                    colour={'BLACK'}
                                    onClick={() => {
                                        setRemoveLogoWarning(true);
                                    }}
                                />
                            )}
                        </ButtonColumn>
                    </LogoAndButtonsRow>
                </HeaderButtonContainer>
            </HeaderLogoAndButtonsContainer>
            {removeLogoWarning && (
                <Alert
                    buttonText={strings.organisationOverviewPage.removeLogoAlert.button.text}
                    ariaButtonText={strings.organisationOverviewPage.removeLogoAlert.button.aria}
                    alertText={strings.organisationOverviewPage.removeLogoAlert.alertText}
                    closeAlert={() => setRemoveLogoWarning(false)}
                    onSubmit={RemoveLogo}
                    error={
                        removeLogoError
                            ? {
                                  title: strings.organisationOverviewPage.removeLogoAlert.removeLogoError.title,
                                  description:
                                      strings.organisationOverviewPage.removeLogoAlert.removeLogoError.description,
                              }
                            : undefined
                    }
                />
            )}
        </FlexColMdFitContent>
    );
}

const HeaderLogoAndButtonsContainer = styled.div`
    gap: ${sizes.spacingLg};
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: ${breakpoints.lg}) {
        flex-direction: column;
        align-items: unset;
        gap: ${sizes.spacingMd};
    }
`;

const HeaderButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        width: 100%;
    }
`;

const ButtonColumn = styled(FlexCol)`
    gap: ${sizes.spacingMd};
`;

const LogoAndButtonsRow = styled(Flex)`
    gap: ${sizes.spacingMd};
    align-items: center;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        align-items: unset;
    }
`;

const LogoContainer = styled.div`
    background-color: ${theme.cardBackground};
    border-radius: ${sizes.borderRadiusXl};
    display: flex;
    align-items: center;
    height: min-content;
    width: min-content;
`;
